import type { Preferences } from '@/lib/types';
import type { PrototypeProjectDetail } from '@/lib/prototype-catalog';

/** Parse brief budget chip → max budget in lakhs (null = no cap). */
export function budgetBandMaxLakhs(label: string | null): number | null {
  if (!label) return null;
  const s = label.replace(/\s+/g, ' ').trim();
  if (/cr\+/i.test(s) || /1\s*cr\s*\+/i.test(s)) return null;
  const crRange = /₹?\s*([\d.]+)\s*l\s*[–-]\s*([\d.]+)\s*cr/i.exec(s);
  if (crRange) return parseFloat(crRange[2]!) * 100;
  const lRange = /₹?\s*([\d.]+)\s*[–-]\s*([\d.]+)\s*l/i.exec(s);
  if (lRange) return parseFloat(lRange[2]!);
  const singleL = /₹?\s*([\d.]+)\s*l/i.exec(s);
  if (singleL) return parseFloat(singleL[1]!);
  return null;
}

/** Parse project price label → max price in lakhs. */
export function projectPriceMaxLakhs(price: string): number | null {
  const s = price.replace(/,/g, '').trim();
  const rangeCr = /([\d.]+)\s*[–-]\s*([\d.]+)\s*cr/i.exec(s);
  if (rangeCr) return parseFloat(rangeCr[2]!) * 100;
  const rangeL = /([\d.]+)\s*[–-]\s*([\d.]+)\s*l/i.exec(s);
  if (rangeL) return parseFloat(rangeL[2]!);
  const singleCr = /([\d.]+)\s*cr/i.exec(s);
  if (singleCr) return parseFloat(singleCr[1]!) * 100;
  const singleL = /([\d.]+)\s*l/i.exec(s);
  if (singleL) return parseFloat(singleL[1]!);
  return null;
}

function locationMatches(projectLocation: string, prefLocation: string | null): boolean {
  if (!prefLocation || prefLocation === 'Open to suggestions') return true;
  const pl = projectLocation.toLowerCase();
  const parts = prefLocation
    .split(/[/,·]/)
    .map((p) => p.trim().toLowerCase())
    .filter((p) => p.length >= 3);
  if (parts.length === 0) return pl.includes(prefLocation.toLowerCase());
  return parts.some((part) => pl.includes(part) || part.includes(pl));
}

function bhkMatches(projectBhk: string | undefined, prefs: Preferences): boolean {
  const wanted = prefs.bhkSelections.length
    ? prefs.bhkSelections
    : prefs.bhk
      ? prefs.bhk.split(' · ').filter(Boolean)
      : [];
  if (wanted.length === 0) return true;
  const pb = (projectBhk ?? '').toLowerCase();
  return wanted.some((w) => {
    const digit = /(\d)/.exec(w)?.[1];
    if (!digit) return pb.includes(w.toLowerCase());
    return pb.includes(`${digit} bhk`) || pb.includes(`${digit}bhk`);
  });
}

export function filterCatalogByPrefs(
  projects: readonly PrototypeProjectDetail[],
  prefs: Preferences,
): PrototypeProjectDetail[] {
  const maxBudget = budgetBandMaxLakhs(prefs.budget);
  return projects.filter((p) => {
    if (!locationMatches(p.location, prefs.location)) return false;
    if (!bhkMatches(p.bhk, prefs)) return false;
    if (maxBudget != null) {
      const priceMax = projectPriceMaxLakhs(p.price);
      if (priceMax != null && priceMax > maxBudget * 1.05) return false;
    }
    return true;
  });
}
